import type { SupabaseClient } from '@supabase/supabase-js'
import { getSupabaseClient } from '../supabase/client'

export interface AccountAccess {
  accountType: string
  displayName: string
  /** One limit per plan key; null means unlimited. Defined only by the server's plan catalog. */
  capacities: {
    notes: number | null
    images: number | null
  }
  privileges: string[]
  usage: {
    notes: number
    images: number
  }
}

export function accountCanAddNote(access: AccountAccess | null): boolean {
  if (!access) return true
  const limit = access.capacities.notes
  return limit === null || access.usage.notes < limit
}

type AccountAccessClient = Pick<SupabaseClient, 'rpc'>
type ClientLoader = () => Promise<AccountAccessClient>

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function nonNegativeInteger(value: unknown): number | null {
  const number = typeof value === 'string' ? Number(value) : value
  return typeof number === 'number' && Number.isSafeInteger(number) && number >= 0
    ? number
    : null
}

/** A limit the server did not send (an older server) is treated as no limit. */
function optionalLimit(value: unknown): number | null | undefined {
  if (value === undefined || value === null) return null
  return nonNegativeInteger(value) ?? undefined
}

export function parseAccountAccess(value: unknown): AccountAccess {
  const payload = record(value)
  const capacities = record(payload.capacities)
  const usage = record(payload.usage)
  const notesLimit = capacities.notes === null ? null : nonNegativeInteger(capacities.notes)
  const notesUsed = nonNegativeInteger(usage.notes)
  const imagesLimit = optionalLimit(capacities.images)
  const privileges = Array.isArray(payload.privileges)
    ? payload.privileges.filter((item): item is string => typeof item === 'string' && item.length > 0)
    : null

  if (
    typeof payload.accountType !== 'string'
    || payload.accountType.length === 0
    || typeof payload.displayName !== 'string'
    || payload.displayName.length === 0
    || (capacities.notes !== null && notesLimit === null)
    || notesUsed === null
    || imagesLimit === undefined
    || privileges === null
  ) {
    throw new Error('Supabase returned invalid account access.')
  }

  return {
    accountType: payload.accountType,
    displayName: payload.displayName,
    capacities: {
      notes: notesLimit,
      images: imagesLimit,
    },
    privileges: [...new Set(privileges)],
    usage: {
      notes: notesUsed,
      images: nonNegativeInteger(usage.images) ?? 0,
    },
  }
}

export async function getMyAccountAccess(
  isDev: boolean,
  loadClient: ClientLoader = getSupabaseClient,
): Promise<AccountAccess> {
  const client = await loadClient()
  const { data, error } = await client.rpc('get_my_account_access', { p_is_dev: isDev })
  if (error) throw new Error('Unable to load account access.')
  return parseAccountAccess(data)
}
